import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoadingPage } from '@/components/ui/loading';
import { setToken } from '@/lib/auth';
import { claimPairingToken } from '../api/pairing-api';

type State =
  | { status: 'loading' }
  | { status: 'no-token' }
  | { status: 'error'; message: string }
  | { status: 'redirecting' };

export function SetupPairLanding() {
  const [params] = useSearchParams();
  const [state, setState] = useState<State>({ status: 'loading' });

  const token = params.get('token');

  useEffect(() => {
    if (!token) {
      setState({ status: 'no-token' });
      return;
    }
    void claim(token);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function claim(rawToken: string) {
    setState({ status: 'loading' });
    try {
      const res = await claimPairingToken(rawToken);
      // Phone-pairing child JWT lives in sessionStorage — closing the tab
      // ends the phone's app-level session. The PairingToken itself stays
      // valid server-side for 8 hours; reopen the QR link to resume.
      setToken(res.accessToken, { ephemeral: true });
      setState({ status: 'redirecting' });
      // Full-page redirect (not SPA navigate) so the new token is actually
      // used: AuthProvider's loadUser() only runs on mount, and a SPA-nav
      // would leave its user=null, sending ProtectedRoute straight to /login.
      // `from=setup-mode` so the scanner's Done/X exit returns the phone to
      // Setup Mode on the property page rather than the bare property view.
      window.location.replace(
        `/pair/scanner?propertyId=${res.propertyId}&buildingId=${res.buildingId}&from=setup-mode`,
      );
      return;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const serverMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const message =
        serverMsg ??
        (status === 410
          ? 'This pairing link has expired or been revoked. Ask your PC to generate a new one.'
          : status === 429
            ? 'Too many attempts. Wait a minute and try again.'
            : 'Pairing failed. Scan a fresh QR code from the PC.');
      setState({ status: 'error', message });
    }
  }

  if (state.status === 'loading' || state.status === 'redirecting') {
    return <LoadingPage />;
  }

  // Landing here without a token means the URL was copied without the query
  // string, or the user typed the path directly. Short codes are no longer
  // supported — direct them back to the PC to get a fresh QR link.
  if (state.status === 'no-token' || state.status === 'error') {
    const message =
      state.status === 'error'
        ? state.message
        : 'Scan the QR code on the PC to pair this phone. The QR includes the full pairing link — nothing to type here.';
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-sm w-full">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-error-50 dark:bg-error-900/30 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-error-500" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {state.status === 'error' ? 'Pairing failed' : 'Pairing link needed'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
